"use client";

/*
 * Stencil web components (shadow: false) use a slot polyfill that physically
 * relocates light-DOM children outside React's tracking. Third-party scripts
 * (HubSpot forms, reCAPTCHA, video embeds, marketing/analytics tags loaded via
 * GTM) do the same via direct DOM APIs. When React's reconciler later tries to
 * remove or reposition a node one of these has already detached/moved,
 * removeChild/insertBefore throw NotFoundError and crash the whole page (see
 * app/error.tsx). This has recurred across multiple, hard-to-predict
 * third-party/Stencil interactions - patched once here as a last-resort net
 * rather than chasing every occurrence individually.
 */
let patched = false;

export default function patchDomReconciliation(): void {
  if (patched || typeof Node === "undefined") return;
  patched = true;

  const originalRemoveChild = Node.prototype.removeChild;

  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      console.warn(
        "patchDomReconciliation: suppressed removeChild on a node that was already detached (likely a third-party script or Stencil slot polyfill race).",
        { parent: this, child },
      );
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode !== null && referenceNode.parentNode !== this) {
      console.warn(
        "patchDomReconciliation: suppressed insertBefore whose reference node was already detached (likely a third-party script or Stencil slot polyfill race); appending instead.",
        { parent: this, newNode, referenceNode },
      );
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
